import { addDecimalStrings, compareDecimalStrings } from "@/features/leave/balance-display";
import type {
  BalanceReportRow,
  LeaveTypeColumn,
} from "@/features/leave/schemas/leave-report.schema";

/**
 * One row per PERSON, rather than one cell per person and leave type.
 *
 * The report's own shape is a matrix: sixty people by thirteen types is 780
 * cells on the demo workspace, and 586 of them are zero. A reader looking for
 * "who is holding a lot" or "who has taken nothing" cannot get either out of
 * that, and the matrix scrolls sideways to boot.
 *
 * So the default view rolls each person up and keeps the per-type breakdown
 * one click below. Nothing is dropped; what changes is what a reader meets
 * first.
 *
 * ## Summing across leave types is valid HERE, and was not on My leave
 *
 * R2 deleted cross-type totals from the employee's own page for a good
 * reason: nobody holds 58 days, they hold 28 privilege and 8 casual and 4
 * sick, and an employee deciding what to book cannot act on the sum.
 *
 * This page has a different reader. HR and finance reading liability want
 * exactly that sum, and "how many days is this workforce holding" IS the
 * question. Same arithmetic, different audience, different validity. The
 * per-type figures are still one expand away for anybody who needs to act
 * rather than count.
 */
export interface BalancePersonRow {
  membershipId: string;
  displayName: string;
  departmentId: string | null;
  /// Everything credited, carry forward included. Not "this year's grant":
  /// that figure does not survive a carry-forward lapse and this module is
  /// careful never to imply it.
  credited: string;
  /// Netted, matching the summary band and the shore's own `taken`.
  taken: string;
  available: string;
  pending: string;
  /// 0 to 100, or null when nothing was ever credited: a utilisation bar over
  /// a zero denominator is a bar that means nothing.
  utilisation: number | null;
  /// Carried days at risk, and the soonest date they go. Null when nothing is
  /// lapsing. CARRY FORWARD only, like the band.
  lapsing: { days: string; on: string | null } | null;
}

/// Roll the matrix up. `balances` is aligned 1:1 with `leaveTypes` by the
/// shore, so the two are zipped positionally, which is safe by construction.
export function buildBalancePeopleRows(
  rows: ReadonlyArray<BalanceReportRow>,
  leaveTypes: ReadonlyArray<LeaveTypeColumn>,
): BalancePersonRow[] {
  return rows.map((row) => {
    const credited = addDecimalStrings(row.balances.map((balance) => balance.credited));
    const taken = addDecimalStrings(row.balances.map((balance) => balance.taken));
    const available = addDecimalStrings(row.balances.map((balance) => balance.available));
    const pending = addDecimalStrings(row.balances.map((balance) => balance.pending));

    return {
      membershipId: row.membershipId,
      displayName: row.displayName,
      departmentId: row.departmentId,
      credited,
      taken,
      available,
      pending,
      utilisation: utilisationOf(taken, credited),
      lapsing: lapsingOf(row, leaveTypes),
    };
  });
}

/**
 * Days at risk for one person: for each type, how much of the balance cannot
 * be spent before the carried days lapse.
 *
 * `available - consumable` is the gap the shore already computes, and it is
 * only reported here when the type carries a carry-forward deadline, so the
 * figure matches the band's "carry forward" wording rather than quietly
 * folding in an allocation window that happens to close.
 */
function lapsingOf(
  row: BalanceReportRow,
  leaveTypes: ReadonlyArray<LeaveTypeColumn>,
): BalancePersonRow["lapsing"] {
  const atRisk: string[] = [];
  let soonest: string | null = null;

  for (const [index, balance] of row.balances.entries()) {
    // A retired type still holds real days, so it is not skipped: the shore
    // keeps sending it precisely so somebody holding days can see them.
    if (!leaveTypes[index]) continue;
    if (!balance.carryForwardExpiresOn) continue;
    if (compareDecimalStrings(balance.consumable, balance.available) >= 0) continue;

    atRisk.push(addDecimalStrings([balance.available, `-${balance.consumable}`]));
    if (!soonest || balance.carryForwardExpiresOn < soonest) {
      soonest = balance.carryForwardExpiresOn;
    }
  }

  if (atRisk.length === 0) return null;
  return { days: addDecimalStrings(atRisk), on: soonest };
}

/**
 * Percent of credited days used, rounded.
 *
 * Null rather than zero when nothing was credited. Zero would draw an empty
 * bar, which reads as "used none of their entitlement" for somebody who has
 * no entitlement at all, and those are different facts.
 *
 * Computed from the STRING figures through the decimal helper, then converted
 * once for display. A percentage is the one place a number is the output, so
 * this is where the string discipline is allowed to end.
 */
function utilisationOf(taken: string, credited: string): number | null {
  if (compareDecimalStrings(credited, "0") <= 0) return null;
  const used = Number(taken);
  const total = Number(credited);
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((used / total) * 100)));
}

export const BALANCE_SORTS = ["name", "mostLeft", "leastTaken", "lapsingSoonest"] as const;
export type BalanceSort = (typeof BALANCE_SORTS)[number];

/**
 * Ranking, which is what turns the table into a tool.
 *
 * ONE HONEST LIMIT, and the caller has to say it on screen: this sorts the
 * rows it was given, and the report is paged. On a workspace bigger than a
 * page, "most days left" means most on THIS page. The shore has no sort
 * parameter, so the alternative is silently ranking a subset and calling it a
 * ranking.
 */
export function sortBalancePeopleRows(
  rows: ReadonlyArray<BalancePersonRow>,
  sort: BalanceSort,
): BalancePersonRow[] {
  const sorted = [...rows];
  switch (sort) {
    case "mostLeft":
      return sorted.sort(
        (a, b) =>
          compareDecimalStrings(b.available, a.available) ||
          a.displayName.localeCompare(b.displayName),
      );
    case "leastTaken":
      return sorted.sort(
        (a, b) =>
          compareDecimalStrings(a.taken, b.taken) || a.displayName.localeCompare(b.displayName),
      );
    case "lapsingSoonest":
      // People losing nothing sort last rather than first: an absent date is
      // "never", and ascending string order would put it before every real
      // deadline.
      return sorted.sort((a, b) => {
        const left = a.lapsing?.on ?? "9999-12-31";
        const right = b.lapsing?.on ?? "9999-12-31";
        return left.localeCompare(right) || a.displayName.localeCompare(b.displayName);
      });
    default:
      return sorted.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }
}

/// Substring match on the name, case insensitive. Narrows what is already in
/// the browser, so it changes no request and cannot widen what the server
/// decided this caller may see.
export function filterBalancePeopleRows(
  rows: ReadonlyArray<BalancePersonRow>,
  search: string,
): BalancePersonRow[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return [...rows];
  return rows.filter((row) => row.displayName.toLowerCase().includes(needle));
}
